import React, { useState, useEffect, useCallback } from 'react';
import { Repeat, Repeat1 } from 'lucide-react';

const LoopControls = ({ 
  deck,
  isEnabled = true,
  bpm,
  position,
  duration,
  onLoopChange,
  wavesurferRef
}) => {
  const [loopActive, setLoopActive] = useState(false);
  const [loopIn, setLoopIn] = useState(null);
  const [loopOut, setLoopOut] = useState(null);
  const [selectedBeatLoop, setSelectedBeatLoop] = useState(null);
  
  // Beat lengths in fractions
  const beatLoops = [
    { label: '1/4', beats: 0.25 },
    { label: '1/2', beats: 0.5 },
    { label: '1', beats: 1 },
    { label: '2', beats: 2 },
    { label: '4', beats: 4 },
    { label: '8', beats: 8 }
  ];
  
  // Calculate loop duration from beats
  const getLoopDuration = useCallback((beats) => {
    if (!bpm) return 2; // Default 2 seconds if no BPM
    const beatDuration = 60 / bpm; // Duration of one beat in seconds
    return beats * beatDuration;
  }, [bpm]);
  
  // Toggle loop in point - click to set, click again to clear
  const toggleLoopIn = () => {
    if (!isEnabled) return;
    
    if (loopIn !== null) {
      // Clear the in point
      setLoopIn(null);
      setLoopActive(false);
      setSelectedBeatLoop(null);
      onLoopChange?.(loopOut !== null ? { in: null, out: loopOut, active: false } : null);
    } else {
      // Set the in point
      setLoopIn(position);
      setSelectedBeatLoop(null);
      
      // If loop out is already set and valid, activate loop
      if (loopOut !== null && position < loopOut) {
        setLoopActive(true);
        onLoopChange?.({ in: position, out: loopOut, active: true });
      } else {
        onLoopChange?.({ in: position, out: loopOut, active: false });
      }
    }
  };
  
  // Toggle loop out point - click to set, click again to clear
  const toggleLoopOut = () => {
    if (!isEnabled) return;
    
    if (loopOut !== null) {
      // Clear the out point
      setLoopOut(null);
      setLoopActive(false);
      setSelectedBeatLoop(null);
      onLoopChange?.(loopIn !== null ? { in: loopIn, out: null, active: false } : null);
    } else {
      // Set the out point
      setLoopOut(position);
      setSelectedBeatLoop(null);
      
      // If loop in is already set and valid, activate loop
      if (loopIn !== null && loopIn < position) {
        setLoopActive(true);
        onLoopChange?.({ in: loopIn, out: position, active: true });
      } else {
        onLoopChange?.({ in: loopIn, out: position, active: false });
      }
    }
  };
  
  // Set beat-based loop from current position
  const setBeatLoop = (beats) => {
    if (!isEnabled) return;
    
    const loopDuration = getLoopDuration(beats);
    const newLoopIn = position;
    const newLoopOut = Math.min(position + loopDuration, duration);
    
    setLoopIn(newLoopIn);
    setLoopOut(newLoopOut);
    setSelectedBeatLoop(beats);
    setLoopActive(true);
    onLoopChange?.({ in: newLoopIn, out: newLoopOut, active: true });
  };
  
  // Auto-detect loop (finds a good loop point based on BPM)
  const autoDetectLoop = () => {
    if (!isEnabled || !bpm) return;
    
    // Default to 4-beat loop from current position
    const beats = 4;
    const loopDuration = getLoopDuration(beats);
    
    // Snap to nearest beat
    const beatDuration = 60 / bpm;
    const currentBeat = Math.round(position / beatDuration);
    const snappedPosition = currentBeat * beatDuration;
    
    const newLoopIn = snappedPosition;
    const newLoopOut = Math.min(snappedPosition + loopDuration, duration);
    
    setLoopIn(newLoopIn);
    setLoopOut(newLoopOut);
    setSelectedBeatLoop(beats);
    setLoopActive(true);
    onLoopChange?.({ in: newLoopIn, out: newLoopOut, active: true });
  };
  
  // Toggle loop on/off
  const toggleLoop = () => {
    if (!isEnabled) return;
    
    if (loopActive) {
      setLoopActive(false);
      onLoopChange?.({ in: loopIn, out: loopOut, active: false });
    } else if (loopIn !== null && loopOut !== null) {
      setLoopActive(true);
      onLoopChange?.({ in: loopIn, out: loopOut, active: true });
    }
  };
  
  // Clear all loop points
  const clearLoop = () => {
    setLoopIn(null);
    setLoopOut(null);
    setLoopActive(false);
    setSelectedBeatLoop(null);
    onLoopChange?.(null);
  };
  
  // Handle loop playback (jump back to loop in when reaching loop out)
  useEffect(() => {
    if (!loopActive || loopIn === null || loopOut === null) return;
    
    if (position >= loopOut - 0.05) {
      // Seek back to loop in point
      if (wavesurferRef?.current) {
        wavesurferRef.current.seekTo(loopIn / duration);
      }
    }
  }, [position, loopActive, loopIn, loopOut, duration, wavesurferRef]);
  
  const deckColor = deck === 'A' ? '#00d4ff' : '#ff6b35';
  
  return (
    <div className={`loop-controls loop-controls-${deck.toLowerCase()}`} data-testid={`loop-controls-${deck.toLowerCase()}`}>
      {/* Row 1: Loop Toggle | IN | OUT */}
      <div className="loop-main-row">
        <button 
          className={`loop-toggle ${loopActive ? 'active' : ''}`}
          onClick={toggleLoop}
          disabled={!isEnabled || (loopIn === null || loopOut === null)}
          style={{ borderColor: loopActive ? deckColor : undefined }}
          title="Toggle loop on/off"
        >
          {loopActive ? <Repeat1 size={12} /> : <Repeat size={12} />}
          <span>LOOP</span>
        </button>
        
        <button 
          className={`loop-point-btn ${loopIn !== null ? 'set' : ''}`}
          onClick={toggleLoopIn}
          disabled={!isEnabled}
          title={loopIn !== null ? "Click to clear IN point" : "Click to set IN point"}
        >
          IN
        </button>
        
        <button 
          className={`loop-point-btn ${loopOut !== null ? 'set' : ''}`}
          onClick={toggleLoopOut}
          disabled={!isEnabled}
          title={loopOut !== null ? "Click to clear OUT point" : "Click to set OUT point"}
        >
          OUT
        </button>
      </div>
      
      {/* Row 2: Beat Loop Buttons */}
      <div className="beat-loops">
        {beatLoops.map(({ label, beats }) => (
          <button
            key={label}
            className={`beat-loop-btn ${selectedBeatLoop === beats ? 'active' : ''}`}
            onClick={() => setBeatLoop(beats)}
            disabled={!isEnabled}
            style={{ 
              borderColor: selectedBeatLoop === beats ? deckColor : undefined,
              color: selectedBeatLoop === beats ? deckColor : undefined
            }}
          >
            {label}
          </button>
        ))}
      </div>
      
      {/* Row 3: Auto & Clear */}
      <div className="loop-actions">
        <button 
          className="loop-action-btn auto"
          onClick={autoDetectLoop}
          disabled={!isEnabled || !bpm}
          title={bpm ? `Auto loop at ${bpm} BPM` : 'BPM required'}
        >
          AUTO
        </button>
        <button 
          className="loop-action-btn clear"
          onClick={clearLoop}
          disabled={!isEnabled || (loopIn === null && loopOut === null)}
          title="Clear all loop points"
        >
          CLEAR
        </button>
      </div>
    </div>
  );
};

export default LoopControls;
